"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";

type Direction = "text-to-leet" | "leet-to-text";
type Level = "basic" | "advanced";

const BASIC_MAP: Record<string, string> = {
  a: "4",
  e: "3",
  i: "1",
  o: "0",
  s: "5",
  t: "7",
};

const ADVANCED_MAP: Record<string, string> = {
  ...BASIC_MAP,
  b: "8",
  g: "9",
  l: "|_",
};

const ELITE_REGEX = /elite/gi;

// Longest leet patterns first so multi-character substitutions win
// during the best-effort reverse translation.
const REVERSE_ENTRIES: { leet: string; plain: string }[] = [
  { leet: "1337", plain: "elite" },
  { leet: "|_", plain: "l" },
  { leet: "4", plain: "a" },
  { leet: "3", plain: "e" },
  { leet: "1", plain: "i" },
  { leet: "0", plain: "o" },
  { leet: "5", plain: "s" },
  { leet: "7", plain: "t" },
  { leet: "8", plain: "b" },
  { leet: "9", plain: "g" },
];

function charToLeet(
  char: string,
  map: Record<string, string>,
  preserveCase: boolean
): string {
  const mapped = map[char.toLowerCase()];
  if (mapped !== undefined) {
    return mapped;
  }
  if (preserveCase) {
    return char;
  }
  return char.toLowerCase();
}

function textToLeet(text: string, level: Level, preserveCase: boolean): string {
  const map = level === "advanced" ? ADVANCED_MAP : BASIC_MAP;
  const source =
    level === "advanced" ? text.replace(ELITE_REGEX, "1337") : text;
  let out = "";
  for (const char of source) {
    out += charToLeet(char, map, preserveCase);
  }
  return out;
}

function leetToText(text: string): string {
  let out = "";
  let i = 0;
  while (i < text.length) {
    const entry = REVERSE_ENTRIES.find((e) => text.startsWith(e.leet, i));
    if (entry === undefined) {
      out += text.charAt(i);
      i += 1;
      continue;
    }
    out += entry.plain;
    i += entry.leet.length;
  }
  return out;
}

/** English strings. A translated content file overrides these through `ui`. */
export const LEET_SPEAK_TRANSLATOR_STRINGS = {
  direction: "Direction",
  textToLeet: "Text → Leet",
  leetToText: "Leet → Text",
  level: "Level",
  basicLevel: "Basic (a e i o s t)",
  advancedLevel: "Advanced (adds b g l + words)",
  preserveCase: "Preserve case of untranslated letters",
  plainText: "Plain text",
  leetSpeak: "Leet speak",
  plainTextPlaceholder: "elite hacker skills",
  leetSpeakPlaceholder: "1337 h4ck3r 5k1115",
  reverseTranslationNote:
    "Reverse translation is a best-effort heuristic — leet is ambiguous, so review the result.",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
  historyHeading: "A Short History of 1337",
  historyBody:
    'Leet speak emerged on 1980s bulletin-board systems, where "elite" users — those with access to the best file areas — signalled status by writing with number and symbol substitutions. "Elite" became "leet", and "leet" written in its own alphabet became 1337. The style spread through hacker culture, IRC, and later online gaming, where 1337 still survives as shorthand for skill. Today it is mostly used for fun, for nostalgic gamer tags, and occasionally to dodge naive text filters.',
} as const;

export default function LeetSpeakTranslatorWidget() {
  const [input, setInput] = useState("");
  const [direction, setDirection] = useState<Direction>("text-to-leet");
  const [level, setLevel] = useState<Level>("basic");
  const [preserveCase, setPreserveCase] = useState(true);
  const [copied, setCopied] = useState(false);

  const strings = useToolUi(LEET_SPEAK_TRANSLATOR_STRINGS);

  const DIRECTIONS: { value: Direction; label: string }[] = [
    { value: "text-to-leet", label: strings.textToLeet },
    { value: "leet-to-text", label: strings.leetToText },
  ];

  const LEVELS: { value: Level; label: string }[] = [
    { value: "basic", label: strings.basicLevel },
    { value: "advanced", label: strings.advancedLevel },
  ];

  const output =
    direction === "text-to-leet"
      ? textToLeet(input, level, preserveCase)
      : leetToText(input);

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setCopied(false);
  }

  return (
    <>
      {/* Direction */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.direction}
        </p>
        <div className="flex flex-wrap gap-3">
          {DIRECTIONS.map((d) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={d.value}
            >
              <input
                checked={direction === d.value}
                className="accent-primary"
                name="leet-direction"
                onChange={() => setDirection(d.value)}
                type="radio"
                value={d.value}
              />
              {d.label}
            </label>
          ))}
        </div>
      </section>

      {/* Level + options */}
      {direction === "text-to-leet" && (
        <section className="space-y-3">
          <p className="font-medium text-foreground text-sm">{strings.level}</p>
          <div className="flex flex-wrap gap-3">
            {LEVELS.map((l) => (
              <label
                className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
                key={l.value}
              >
                <input
                  checked={level === l.value}
                  className="accent-primary"
                  name="leet-level"
                  onChange={() => setLevel(l.value)}
                  type="radio"
                  value={l.value}
                />
                {l.label}
              </label>
            ))}
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-foreground text-sm">
            <input
              checked={preserveCase}
              className="accent-primary"
              onChange={(e) => setPreserveCase(e.target.checked)}
              type="checkbox"
            />
            {strings.preserveCase}
          </label>
        </section>
      )}

      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="leet-input"
        >
          {direction === "text-to-leet" ? strings.plainText : strings.leetSpeak}
        </label>
        <textarea
          className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="leet-input"
          onChange={(e) => {
            setInput(e.target.value);
            setCopied(false);
          }}
          placeholder={
            direction === "text-to-leet"
              ? strings.plainTextPlaceholder
              : strings.leetSpeakPlaceholder
          }
          value={input}
        />
      </div>

      {/* Output */}
      {output.length > 0 && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="leet-output"
          >
            {direction === "text-to-leet"
              ? strings.leetSpeak
              : strings.plainText}
          </label>
          <textarea
            className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="leet-output"
            readOnly
            value={output}
          />
          {direction === "leet-to-text" && (
            <p className="text-muted-foreground text-xs">
              {strings.reverseTranslationNote}
            </p>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      {/* Culture note */}
      <section className="space-y-3 rounded-lg border border-border bg-card p-6">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.historyHeading}
        </h2>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.historyBody}
        </p>
      </section>
    </>
  );
}
